// ============================================================
//  MASAMADREAM — Activity Modal (Bottom Sheet)
//  Muestra instrucciones paso a paso de una actividad
// ============================================================

import { toast } from './toast.js';

let _currentActivity = null;
let _onComplete      = null;

// Timer state
let _timerInterval  = null;
let _timerSeconds   = 0;
let _timerActivityId = null;
let _timerOnCompleteCallback = null;

// ── Open ──────────────────────────────────────────────────────
export function openActivityModal(activity, isCompleted, onComplete) {
  _currentActivity = activity;
  _onComplete      = onComplete;
  _timerOnCompleteCallback = onComplete;

  const backdrop = document.getElementById('mm-activity-modal');
  if (!backdrop) return;

  // Populate content
  _fill(activity, isCompleted);

  // Show
  backdrop.classList.remove('mm-hidden');
  requestAnimationFrame(() => {
    backdrop.classList.add('is-open');
  });

  // Prevent body scroll
  document.body.style.overflow = 'hidden';
}

// ── Close ─────────────────────────────────────────────────────
export function closeActivityModal() {
  const backdrop = document.getElementById('mm-activity-modal');
  if (!backdrop) return;

  backdrop.classList.remove('is-open');

  backdrop.addEventListener('transitionend', () => {
    backdrop.classList.add('mm-hidden');
    document.body.style.overflow = '';
    _currentActivity = null;
    _timerOnCompleteCallback = null;
    _stopTimer();
  }, { once: true });
}

// ── Fill content ──────────────────────────────────────────────
function _fill(act, isCompleted) {
  _setText('mm-modal-emoji',    act.emoji || '📋');
  _setText('mm-modal-action',   act.action);
  _setText('mm-modal-duration', `⏱ ${act.duration}`);
  _setText('mm-modal-desc',     act.description);

  // Initialize timer UI for this activity
  _initTimerForActivity(act);

  // Day badge
  const badge = document.getElementById('mm-modal-day-badge');
  if (badge) badge.textContent = `Día ${act.day}`;

  // Steps
  const list = document.getElementById('mm-modal-steps');
  if (list) {
    list.innerHTML = '';
    (act.instructions || []).forEach((step, i) => {
      const li = document.createElement('li');
      li.className = 'mm-modal-step';

      // Steps that start with • or 💡 get special styling
      if (step.startsWith('💡')) {
        li.className += ' mm-modal-step--tip';
        li.textContent = step;
      } else if (step.startsWith('•') || step.match(/^[0-9]+ g|^[0-9]+ ml/)) {
        li.className += ' mm-modal-step--ingredient';
        li.textContent = step;
      } else {
        const num = document.createElement('span');
        num.className = 'mm-modal-step-num';
        num.textContent = i + 1;
        li.appendChild(num);
        const txt = document.createElement('span');
        txt.textContent = step;
        li.appendChild(txt);
      }

      list.appendChild(li);
    });
  }

  // Complete button state
  const btn = document.getElementById('mm-modal-complete-btn');
  if (btn) {
    if (isCompleted) {
      btn.textContent = '↩️ Desmarcar';
      btn.classList.remove('mm-btn--primary');
      btn.classList.add('mm-btn--outline');
    } else {
      btn.textContent = '✓ Marcar como completado';
      btn.classList.add('mm-btn--primary');
      btn.classList.remove('mm-btn--outline');
    }
  }
}

// ── Wire static DOM events (called once on DOMContentLoaded) ──
export function initActivityModal() {
  const backdrop = document.getElementById('mm-activity-modal');
  const sheet    = document.getElementById('mm-modal-sheet');

  // Close on backdrop click
  backdrop?.addEventListener('click', (e) => {
    if (e.target === backdrop) closeActivityModal();
  });

  // Close button
  document.getElementById('mm-modal-close')?.addEventListener('click', closeActivityModal);

  // ESC key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeActivityModal();
  });

  // Complete button
  document.getElementById('mm-modal-complete-btn')?.addEventListener('click', () => {
    if (_currentActivity && _onComplete) {
      _onComplete(_currentActivity);
      closeActivityModal();
    }
  });

  // Touch swipe-down to close
  let _startY = 0;
  sheet?.addEventListener('touchstart', (e) => {
    _startY = e.touches[0].clientY;
  }, { passive: true });

  sheet?.addEventListener('touchend', (e) => {
    const delta = e.changedTouches[0].clientY - _startY;
    if (delta > 60) closeActivityModal(); // swipe down > 60px
  }, { passive: true });
}

// ── Util ──────────────────────────────────────────────────────
function _setText(id, val) {
  const el = document.getElementById(id);
  if (el) el.textContent = val;
}

// ── Timer ──────────────────────────────────────────────────────
function _parseDurationToMinutes(durationStr) {
  if (!durationStr) return 0;
  const str = durationStr.toLowerCase();
  
  // Handle ranges like "1–2 horas" or "12–16 horas"
  const rangeMatch = str.match(/(\d+)[–-](\d+)\s*(hora|horas|min)/);
  if (rangeMatch) {
    return parseInt(rangeMatch[2]) * (str.includes('hora') ? 60 : 1);
  }
  
  // Handle "X hora" or "X horas"
  const hourMatch = str.match(/(\d+)\s*hora?s?/);
  if (hourMatch) return parseInt(hourMatch[1]) * 60;
  
  // Handle "X min" or "X minuto(s)"
  const minMatch = str.match(/(\d+)\s*min/);
  if (minMatch) return parseInt(minMatch[1]);
  
  return 10; // default fallback
}

function _initTimerForActivity(act) {
  const display = document.getElementById('mm-timer-display');
  const btn = document.getElementById('mm-timer-start-btn');
  if (!display || !btn) return;
  
  // Clear any existing timer
  _stopTimer();
  _timerActivityId = act.id;
  
  const minutes = _parseDurationToMinutes(act.duration);
  _timerSeconds = minutes * 60;
  
  display.textContent = _formatTime(_timerSeconds);
  display.classList.remove('is-active', 'is-done');
  btn.textContent = '⏱ Iniciar timer';
  btn.classList.remove('mm-btn--primary');
  btn.classList.add('mm-btn--outline');
  
  btn.onclick = () => {
    if (_timerInterval) {
      _stopTimer();
    } else {
      _startTimer();
    }
  };
}

function _startTimer() {
  const display = document.getElementById('mm-timer-display');
  const btn = document.getElementById('mm-timer-start-btn');
  if (!display || !btn) return;
  
  display.classList.add('is-active');
  btn.textContent = '⏹ Detener';
  btn.classList.remove('mm-btn--outline');
  btn.classList.add('mm-btn--primary');
  
  _timerInterval = setInterval(() => {
    _timerSeconds--;
    display.textContent = _formatTime(_timerSeconds);
    
    if (_timerSeconds <= 0) {
      _onTimerComplete();
    }
  }, 1000);
}

function _stopTimer() {
  if (_timerInterval) {
    clearInterval(_timerInterval);
    _timerInterval = null;
  }
}

function _onTimerComplete() {
  _stopTimer();
  
  const display = document.getElementById('mm-timer-display');
  const btn = document.getElementById('mm-timer-start-btn');
  if (display) {
    display.textContent = '¡Listo!';
    display.classList.remove('is-active');
    display.classList.add('is-done');
  }
  if (btn) {
    btn.textContent = '🔄 Reiniciar';
    btn.classList.remove('mm-btn--primary');
    btn.classList.add('mm-btn--outline');
  }
  
  // Play alarm sound
  _playAlarm();
  
  // Mark activity as completed via callback
  if (_timerOnCompleteCallback && _currentActivity) {
    _timerOnCompleteCallback(_currentActivity);
  }
  
  // Show toast notification
  if (_currentActivity) {
    toast(`⏰ Timer de "${_currentActivity.action}" terminado!`, 5000);
  }
}

function _formatTime(totalSeconds) {
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function _playAlarm() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const duration = 0.15;
    const now = ctx.currentTime;
    
    // Play 3 beeps
    for (let i = 0; i < 3; i++) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.frequency.value = 800;
      osc.type = 'sine';
      
      const startTime = now + (i * 0.3);
      gain.gain.setValueAtTime(0.3, startTime);
      gain.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
      
      osc.start(startTime);
      osc.stop(startTime + duration);
    }
  } catch (e) {
    console.warn('Audio not supported:', e);
  }
}

// ── Export for external access ─────────────────────────────────
export function closeTimer() {
  _stopTimer();
}
